import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    FormControlLabel,
    Switch,
    MenuItem,
    Grid,
    IconButton,
    Avatar,
    CircularProgress,
    Backdrop,
    Snackbar
} from '@mui/material';
import { extractErrorMessage, createUserFriendlyErrorMessage } from '../utils/ErrorUtil';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { FlashBetMatchDto, UpdateMatchMetadataRequest, UpdateLeagueRequest, UpdateStadiumRequest } from '../models/dtos/FlashBetMatchDto';
import { getFlashBetCategoriesBySport, deleteFlashBetMatch, checkFlashBetMatchExists, getAllFlashBetLeagues, getAllFlashBetStadiums, refetchMatchDetails } from '../Api';
import FlashBetCategoryDto from '../models/dtos/FlashBetCategoryDto';
import FlashBetLeagueDto from '../models/dtos/FlashBetLeagueDto';
import FlashBetStadiumDto from '../models/dtos/FlashBetStadiumDto';

interface EditMatchModalProps {
    open: boolean;
    match: FlashBetMatchDto | null;
    onSave: (data: UpdateMatchMetadataRequest) => Promise<void>;
    onApply: (data: UpdateMatchMetadataRequest) => Promise<void>;
    onClose: () => void;
    onDelete?: (matchId: string) => Promise<void>;
    onRefetch?: (updatedMatch: FlashBetMatchDto) => void;
}

const EditMatchModal: React.FC<EditMatchModalProps> = ({ 
    open, 
    match, 
    onSave, 
    onApply,
    onClose,
    onDelete,
    onRefetch
}) => {
    const [formData, setFormData] = useState({
        betsApiGameId: '',
        categoryId: '',
        homeTeam: {
            name: '',
            photoUrl: '',
            localizations: {} as { [key: string]: string }
        },
        awayTeam: {
            name: '',
            photoUrl: '',
            localizations: {} as { [key: string]: string }
        },
        league: {
            name: '',
            logoUrl: '',
            localizations: {} as { [key: string]: string }
        },
        stadium: {
            name: '',
            city: '',
            country: '',
            geoLocation: ''
        },
        startDate: '',
        enabled: true
    });
    const [categories, setCategories] = useState<FlashBetCategoryDto[]>([]);
    const [leagues, setLeagues] = useState<FlashBetLeagueDto[]>([]);
    const [stadiums, setStadiums] = useState<FlashBetStadiumDto[]>([]);
    const [homeTeamLocalizations, setHomeTeamLocalizations] = useState<Array<{ lang: string; name: string }>>([]);
    const [awayTeamLocalizations, setAwayTeamLocalizations] = useState<Array<{ lang: string; name: string }>>([]);
    const [leagueLocalizations, setLeagueLocalizations] = useState<Array<{ lang: string; name: string }>>([]);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState(false);
    const [originalBetsApiId, setOriginalBetsApiId] = useState<string>('');
    const [showBetsApiIdHint, setShowBetsApiIdHint] = useState<boolean>(false);
    const [betsApiIdValidating, setBetsApiIdValidating] = useState<boolean>(false);
    const [betsApiIdError, setBetsApiIdError] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [refetchLoading, setRefetchLoading] = useState<boolean>(false);

    useEffect(() => {
        if (open && match) {
            loadData();
            populateFormData();
        }
    }, [open, match]);

    const loadData = async () => {
        if (!match) return;
        
        try {
            const [categoriesData, leaguesData, stadiumsData] = await Promise.all([
                getFlashBetCategoriesBySport(match.sport.id),
                getAllFlashBetLeagues(0, 100), // Get first 100 leagues
                getAllFlashBetStadiums(0, 100)  // Get first 100 stadiums
            ]);
            
            setCategories(categoriesData);
            setLeagues(leaguesData?.content || []);
            setStadiums(stadiumsData?.content || []);
        } catch (error) {
            console.error('Failed to load data:', error);
            const errorMsg = await extractErrorMessage(error, 'Failed to load modal data');
            setErrorMessage(errorMsg);
        }
    };

    const populateFormData = () => {
        if (!match) return;

        // Convert the current date to ISO format for datetime-local input
        const now = new Date().toISOString().slice(0, 16);
        const betsApiId = match.betsApiId || '';
        
        setFormData({
            betsApiGameId: betsApiId,
            categoryId: match.category.id,
            homeTeam: {
                name: match.homeTeamDto?.name || '',
                photoUrl: match.homeTeamDto?.photoUrl || '',
                localizations: match.homeTeamDto?.localizations || {}
            },
            awayTeam: {
                name: match.awayTeamDto?.name || '',
                photoUrl: match.awayTeamDto?.photoUrl || '',
                localizations: match.awayTeamDto?.localizations || {}
            },
            league: {
                name: match.league?.name || '',
                logoUrl: match.league?.logoUrl || '',
                localizations: match.league?.localizations || {}
            },
            stadium: {
                name: match.stadium?.name || '',
                city: match.stadium?.city || '',
                country: match.stadium?.country || '',
                geoLocation: match.stadium?.geoLocation || ''
            },
            startDate: now, // Default to current time since we don't have the actual start date from the DTO
            enabled: match.enabled
        });

        // Store original betsApiId for comparison
        setOriginalBetsApiId(betsApiId);
        setShowBetsApiIdHint(false);

        // Set localizations for display
        setHomeTeamLocalizations(Object.entries(match.homeTeamDto?.localizations || {}).map(([lang, name]) => ({ lang, name })));
        setAwayTeamLocalizations(Object.entries(match.awayTeamDto?.localizations || {}).map(([lang, name]) => ({ lang, name })));
        setLeagueLocalizations(Object.entries(match.league?.localizations || {}).map(([lang, name]) => ({ lang, name })));
    };

    const populateFormDataWithMatch = (matchData: FlashBetMatchDto) => {
        // Convert the current date to ISO format for datetime-local input
        const now = new Date().toISOString().slice(0, 16);
        const betsApiId = matchData.betsApiId || '';
        
        setFormData({
            betsApiGameId: betsApiId,
            categoryId: matchData.category.id,
            homeTeam: {
                name: matchData.homeTeamDto?.name || '',
                photoUrl: matchData.homeTeamDto?.photoUrl || '',
                localizations: matchData.homeTeamDto?.localizations || {}
            },
            awayTeam: {
                name: matchData.awayTeamDto?.name || '',
                photoUrl: matchData.awayTeamDto?.photoUrl || '',
                localizations: matchData.awayTeamDto?.localizations || {}
            },
            league: {
                name: matchData.league?.name || '',
                logoUrl: matchData.league?.logoUrl || '',
                localizations: matchData.league?.localizations || {}
            },
            stadium: {
                name: matchData.stadium?.name || '',
                city: matchData.stadium?.city || '',
                country: matchData.stadium?.country || '',
                geoLocation: matchData.stadium?.geoLocation || ''
            },
            startDate: now, // Default to current time since we don't have the actual start date from the DTO
            enabled: matchData.enabled
        });

        // Store original betsApiId for comparison
        setOriginalBetsApiId(betsApiId);
        setShowBetsApiIdHint(false);

        // Set localizations for display
        setHomeTeamLocalizations(Object.entries(matchData.homeTeamDto?.localizations || {}).map(([lang, name]) => ({ lang, name })));
        setAwayTeamLocalizations(Object.entries(matchData.awayTeamDto?.localizations || {}).map(([lang, name]) => ({ lang, name })));
        setLeagueLocalizations(Object.entries(matchData.league?.localizations || {}).map(([lang, name]) => ({ lang, name })));
    };

    const validateBetsApiId = async (betsApiId: string) => {
        // Don't validate if empty or same as original value
        if (!betsApiId.trim() || betsApiId === originalBetsApiId) {
            setBetsApiIdError(null);
            setBetsApiIdValidating(false);
            return;
        }

        setBetsApiIdValidating(true);
        setBetsApiIdError(null);

        try {
            const result = await checkFlashBetMatchExists(betsApiId);
            if (result && result.exists) {
                setBetsApiIdError('This Bets API Game ID is already in use');
            } else {
                setBetsApiIdError(null);
            }
        } catch (error) {
            console.error('Failed to validate betsApiId:', error);
            const errorMsg = await extractErrorMessage(error, 'Validation failed');
            setBetsApiIdError(errorMsg);
        } finally {
            setBetsApiIdValidating(false);
        }
    };

    const handleInputChange = (field: string, value: any) => {
        setFormData(prev => {
            if (field.includes('.')) {
                const [parent, child] = field.split('.');
                return {
                    ...prev,
                    [parent]: {
                        ...prev[parent as keyof typeof prev] as any,
                        [child]: value
                    }
                };
            } else {
                return {
                    ...prev,
                    [field]: value
                };
            }
        });
        
        // Show hint for betsApiId changes and validate
        if (field === 'betsApiGameId') {
            setShowBetsApiIdHint(value !== originalBetsApiId);
            // Validate betsApiId if it has changed
            validateBetsApiId(value);
        }
        
        // Clear errors when user starts typing
        if (errors[field]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const handleAddLocalization = (team: 'homeTeam' | 'awayTeam' | 'league') => {
        if (team === 'homeTeam') {
            setHomeTeamLocalizations([...homeTeamLocalizations, { lang: '', name: '' }]);
        } else if (team === 'awayTeam') {
            setAwayTeamLocalizations([...awayTeamLocalizations, { lang: '', name: '' }]);
        } else if (team === 'league') {
            setLeagueLocalizations([...leagueLocalizations, { lang: '', name: '' }]);
        }
    };

    const handleRemoveLocalization = (team: 'homeTeam' | 'awayTeam' | 'league', index: number) => {
        if (team === 'homeTeam') {
            const newLocalizations = homeTeamLocalizations.filter((_, i) => i !== index);
            setHomeTeamLocalizations(newLocalizations);
            updateLocalizations(team, newLocalizations);
        } else if (team === 'awayTeam') {
            const newLocalizations = awayTeamLocalizations.filter((_, i) => i !== index);
            setAwayTeamLocalizations(newLocalizations);
            updateLocalizations(team, newLocalizations);
        } else if (team === 'league') {
            const newLocalizations = leagueLocalizations.filter((_, i) => i !== index);
            setLeagueLocalizations(newLocalizations);
            updateLocalizations(team, newLocalizations);
        }
    };

    const handleLocalizationChange = (team: 'homeTeam' | 'awayTeam' | 'league', index: number, field: 'lang' | 'name', value: string) => {
        if (team === 'homeTeam') {
            const newLocalizations = [...homeTeamLocalizations];
            newLocalizations[index][field] = value;
            setHomeTeamLocalizations(newLocalizations);
            updateLocalizations(team, newLocalizations);
        } else if (team === 'awayTeam') {
            const newLocalizations = [...awayTeamLocalizations];
            newLocalizations[index][field] = value;
            setAwayTeamLocalizations(newLocalizations);
            updateLocalizations(team, newLocalizations);
        } else if (team === 'league') {
            const newLocalizations = [...leagueLocalizations];
            newLocalizations[index][field] = value;
            setLeagueLocalizations(newLocalizations);
            updateLocalizations(team, newLocalizations);
        }
    };

    const updateLocalizations = (team: 'homeTeam' | 'awayTeam' | 'league', localizations: Array<{ lang: string; name: string }>) => {
        const localizationObj = localizations.reduce((acc, loc) => {
            if (loc.lang && loc.name) {
                acc[loc.lang] = loc.name;
            }
            return acc;
        }, {} as { [key: string]: string });

        setFormData(prev => ({
            ...prev,
            [team]: {
                ...prev[team],
                localizations: localizationObj
            }
        }));
    };

    const validateForm = (): boolean => {
        const newErrors: { [key: string]: string } = {};

        if (!formData.homeTeam.name.trim()) {
            newErrors['homeTeam.name'] = 'Home team name is required';
        }

        if (!formData.awayTeam.name.trim()) {
            newErrors['awayTeam.name'] = 'Away team name is required';
        }

        if (!formData.categoryId) {
            newErrors.categoryId = 'Category is required';
        }

        if (!formData.startDate) {
            newErrors.startDate = 'Start date is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const prepareRequestData = (): UpdateMatchMetadataRequest => {
        return {
            betsApiGameId: formData.betsApiGameId,
            categoryId: formData.categoryId,
            homeTeam: formData.homeTeam,
            awayTeam: formData.awayTeam,
            league: formData.league,
            stadium: formData.stadium,
            startDate: formData.startDate,
            enabled: formData.enabled
        };
    };

    const handleSave = async () => {
        if (!validateForm()) return;
        setIsLoading(true);
        try {
            await onSave(prepareRequestData());
            handleClose();
        } catch (error) {
            console.error('Failed to save match:', error);
            const errorMsg = await createUserFriendlyErrorMessage('save match', error);
            setErrorMessage(errorMsg);
        } finally {
            setIsLoading(false);
        }
    };

    const handleApply = async () => {
        if (!validateForm()) return;
        setIsLoading(true);
        try {
            await onApply(prepareRequestData());
        } catch (error) {
            console.error('Failed to apply changes:', error);
            const errorMsg = await createUserFriendlyErrorMessage('apply changes', error);
            setErrorMessage(errorMsg);
        } finally {
            setIsLoading(false);
        }
    };

    const handleRefetch = async () => {
        if (!match) return;
        
        setRefetchLoading(true);
        try {
            const updatedMatch = await refetchMatchDetails(match.id);
            if (updatedMatch) {
                // Update the form data with the new match details
                populateFormDataWithMatch(updatedMatch);
                
                // Notify parent component about the updated match
                if (onRefetch) {
                    onRefetch(updatedMatch);
                }
                
                setErrorMessage(null);
            } else {
                setErrorMessage('Failed to refetch match details');
            }
        } catch (error) {
            console.error('Failed to refetch match details:', error);
            const errorMsg = await createUserFriendlyErrorMessage('refetch match details', error);
            setErrorMessage(errorMsg);
        } finally {
            setRefetchLoading(false);
        }
    };

    const handleClose = () => {
        if (isLoading || refetchLoading) return; // Prevent closing while loading
        
        setErrors({});
        setHomeTeamLocalizations([]);
        setAwayTeamLocalizations([]);
        setLeagueLocalizations([]);
        setDeleteDialogOpen(false);
        setOriginalBetsApiId('');
        setShowBetsApiIdHint(false);
        setBetsApiIdValidating(false);
        setBetsApiIdError(null);
        setRefetchLoading(false);
        onClose();
    };

    const handleDeleteClick = () => {
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (match && onDelete) {
            setIsLoading(true);
            try {
                await onDelete(match.id);
                setDeleteDialogOpen(false);
                handleClose();
            } catch (error) {
                console.error('Failed to delete match:', error);
                const errorMsg = await createUserFriendlyErrorMessage('delete match', error);
                setErrorMessage(errorMsg);
                setIsLoading(false);
            }
        }
    };

    if (!match) return null;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                Edit Match
            </DialogTitle>
            <DialogContent>
                <Box component="form" sx={{ mt: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Bets API Game ID"
                                value={formData.betsApiGameId}
                                onChange={(e) => handleInputChange('betsApiGameId', e.target.value)}
                                error={!!betsApiIdError}
                                sx={{ mb: 1 }}
                            />
                            {/* Single hint space - show validation error, checking status, or warning */}
                            {betsApiIdError ? (
                                <Typography 
                                    variant="caption" 
                                    color="error.main" 
                                    sx={{ display: 'block', mb: 1 }}
                                >
                                    {betsApiIdError}
                                </Typography>
                            ) : betsApiIdValidating ? (
                                <Typography 
                                    variant="caption" 
                                    color="info.main" 
                                    sx={{ display: 'block', mb: 1 }}
                                >
                                    Checking availability...
                                </Typography>
                            ) : showBetsApiIdHint ? (
                                <Typography 
                                    variant="caption" 
                                    color="warning.main" 
                                    sx={{ display: 'block', mb: 1, fontStyle: 'italic' }}
                                >
                                    Change of the betsApiId will lead to whole match re-fetch and re-processing on save
                                </Typography>
                            ) : (
                                <Typography 
                                    variant="caption" 
                                    sx={{ display: 'block', mb: 1, visibility: 'hidden' }}
                                >
                                    &nbsp;
                                </Typography>
                            )}
                            
                            {/* Re-fetch button */}
                            <Box sx={{ mb: 2 }}>
                                <Button 
                                    onClick={handleRefetch} 
                                    variant="outlined" 
                                    color="primary"
                                    disabled={isLoading || refetchLoading}
                                    sx={{ mr: 1 }}
                                >
                                    {refetchLoading ? (
                                        <>
                                            <CircularProgress size={20} sx={{ mr: 1 }} />
                                            Re-fetching...
                                        </>
                                    ) : (
                                        'Re-fetch Match Details'
                                    )}
                                </Button>
                                <Typography 
                                    variant="caption" 
                                    color="text.secondary"
                                    sx={{ ml: 1 }}
                                >
                                    Fetch latest match data from the provider
                                </Typography>
                            </Box>
                        </Grid>

                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.enabled}
                                        onChange={(e) => handleInputChange('enabled', e.target.checked)}
                                    />
                                }
                                label="Enabled"
                                sx={{ mb: 2 }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Category"
                                select
                                value={formData.categoryId}
                                onChange={(e) => handleInputChange('categoryId', e.target.value)}
                                error={!!errors.categoryId}
                                helperText={errors.categoryId}
                                sx={{ mb: 2 }}
                            >
                                {categories.map((category) => (
                                    <MenuItem key={category.id} value={category.id}>
                                        {category.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Start Date"
                                type="datetime-local"
                                value={formData.startDate}
                                onChange={(e) => handleInputChange('startDate', e.target.value)}
                                error={!!errors.startDate}
                                helperText={errors.startDate}
                                sx={{ mb: 2 }}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        </Grid>

                        {/* Home Team Section */}
                        <Grid item xs={12}>
                            <Box display="flex" alignItems="center" mb={1}>
                                <Avatar 
                                    src={formData.homeTeam.photoUrl} 
                                    alt={formData.homeTeam.name}
                                    sx={{ width: 32, height: 32, mr: 2 }}
                                />
                                <Typography variant="h6">Home Team</Typography>
                            </Box>
                        </Grid>
                        
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Home Team Name"
                                value={formData.homeTeam.name}
                                onChange={(e) => handleInputChange('homeTeam.name', e.target.value)}
                                error={!!errors['homeTeam.name']}
                                helperText={errors['homeTeam.name']}
                                sx={{ mb: 2 }}
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Home Team Photo URL"
                                value={formData.homeTeam.photoUrl}
                                onChange={(e) => handleInputChange('homeTeam.photoUrl', e.target.value)}
                                sx={{ mb: 2 }}
                            />
                        </Grid>

                        {homeTeamLocalizations.map((loc, index) => (
                            <Grid item xs={12} key={`home-${index}`}>
                                <Box display="flex" gap={1} alignItems="center">
                                    <TextField
                                        label="Language"
                                        select
                                        value={loc.lang}
                                        onChange={(e) => handleLocalizationChange('homeTeam', index, 'lang', e.target.value)}
                                        sx={{ minWidth: 120 }}
                                    >
                                        <MenuItem value="">Select language</MenuItem>
                                        <MenuItem value="de">German (de)</MenuItem>
                                        <MenuItem value="fr">French (fr)</MenuItem>
                                        <MenuItem value="it">Italian (it)</MenuItem>
                                        <MenuItem value="ro">Romanian (ro)</MenuItem>
                                        <MenuItem value="ru">Russian (ru)</MenuItem>
                                        <MenuItem value="es">Spanish (es)</MenuItem>
                                        <MenuItem value="pt">Portuguese (pt)</MenuItem>
                                        <MenuItem value="pl">Polish (pl)</MenuItem>
                                        <MenuItem value="tr">Turkish (tr)</MenuItem>
                                    </TextField>
                                    <TextField
                                        fullWidth
                                        label="Localized Name"
                                        value={loc.name}
                                        onChange={(e) => handleLocalizationChange('homeTeam', index, 'name', e.target.value)}
                                    />
                                    <IconButton onClick={() => handleRemoveLocalization('homeTeam', index)}>
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                            </Grid>
                        ))}

                        <Grid item xs={12}>
                            <Button 
                                startIcon={<AddIcon />} 
                                onClick={() => handleAddLocalization('homeTeam')}
                                size="small"
                            >
                                Add Home Team Localization
                            </Button>
                        </Grid>

                        {/* Away Team Section */}
                        <Grid item xs={12}>
                            <Box display="flex" alignItems="center" mb={1}>
                                <Avatar 
                                    src={formData.awayTeam.photoUrl} 
                                    alt={formData.awayTeam.name}
                                    sx={{ width: 32, height: 32, mr: 2 }}
                                />
                                <Typography variant="h6">Away Team</Typography>
                            </Box>
                        </Grid>
                        
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Away Team Name"
                                value={formData.awayTeam.name}
                                onChange={(e) => handleInputChange('awayTeam.name', e.target.value)}
                                error={!!errors['awayTeam.name']}
                                helperText={errors['awayTeam.name']}
                                sx={{ mb: 2 }}
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Away Team Photo URL"
                                value={formData.awayTeam.photoUrl}
                                onChange={(e) => handleInputChange('awayTeam.photoUrl', e.target.value)}
                                sx={{ mb: 2 }}
                            />
                        </Grid>

                        {awayTeamLocalizations.map((loc, index) => (
                            <Grid item xs={12} key={`away-${index}`}>
                                <Box display="flex" gap={1} alignItems="center">
                                    <TextField
                                        label="Language"
                                        select
                                        value={loc.lang}
                                        onChange={(e) => handleLocalizationChange('awayTeam', index, 'lang', e.target.value)}
                                        sx={{ minWidth: 120 }}
                                    >
                                        <MenuItem value="">Select language</MenuItem>
                                        <MenuItem value="de">German (de)</MenuItem>
                                        <MenuItem value="fr">French (fr)</MenuItem>
                                        <MenuItem value="it">Italian (it)</MenuItem>
                                        <MenuItem value="ro">Romanian (ro)</MenuItem>
                                        <MenuItem value="ru">Russian (ru)</MenuItem>
                                        <MenuItem value="es">Spanish (es)</MenuItem>
                                        <MenuItem value="pt">Portuguese (pt)</MenuItem>
                                        <MenuItem value="pl">Polish (pl)</MenuItem>
                                        <MenuItem value="tr">Turkish (tr)</MenuItem>
                                    </TextField>
                                    <TextField
                                        fullWidth
                                        label="Localized Name"
                                        value={loc.name}
                                        onChange={(e) => handleLocalizationChange('awayTeam', index, 'name', e.target.value)}
                                    />
                                    <IconButton onClick={() => handleRemoveLocalization('awayTeam', index)}>
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                            </Grid>
                        ))}

                        <Grid item xs={12}>
                            <Button 
                                startIcon={<AddIcon />} 
                                onClick={() => handleAddLocalization('awayTeam')}
                                size="small"
                            >
                                Add Away Team Localization
                            </Button>
                        </Grid>

                        {/* League Section */}
                        <Grid item xs={12}>
                            <Box display="flex" alignItems="center" mb={1} mt={3}>
                                <Avatar 
                                    src={formData.league.logoUrl} 
                                    alt={formData.league.name}
                                    sx={{ width: 32, height: 32, mr: 2 }}
                                />
                                <Typography variant="h6">League</Typography>
                            </Box>
                        </Grid>
                        
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="League Name"
                                value={formData.league.name}
                                onChange={(e) => handleInputChange('league.name', e.target.value)}
                                sx={{ mb: 2 }}
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="League Logo URL"
                                value={formData.league.logoUrl}
                                onChange={(e) => handleInputChange('league.logoUrl', e.target.value)}
                                sx={{ mb: 2 }}
                            />
                        </Grid>

                        {leagueLocalizations.map((loc, index) => (
                            <Grid item xs={12} key={`league-${index}`}>
                                <Box display="flex" gap={1} alignItems="center">
                                    <TextField
                                        label="Language"
                                        select
                                        value={loc.lang}
                                        onChange={(e) => handleLocalizationChange('league', index, 'lang', e.target.value)}
                                        sx={{ minWidth: 120 }}
                                    >
                                        <MenuItem value="">Select language</MenuItem>
                                        <MenuItem value="de">German (de)</MenuItem>
                                        <MenuItem value="fr">French (fr)</MenuItem>
                                        <MenuItem value="it">Italian (it)</MenuItem>
                                        <MenuItem value="ro">Romanian (ro)</MenuItem>
                                        <MenuItem value="ru">Russian (ru)</MenuItem>
                                        <MenuItem value="es">Spanish (es)</MenuItem>
                                        <MenuItem value="pt">Portuguese (pt)</MenuItem>
                                        <MenuItem value="pl">Polish (pl)</MenuItem>
                                        <MenuItem value="tr">Turkish (tr)</MenuItem>
                                    </TextField>
                                    <TextField
                                        fullWidth
                                        label="Localized Name"
                                        value={loc.name}
                                        onChange={(e) => handleLocalizationChange('league', index, 'name', e.target.value)}
                                    />
                                    <IconButton onClick={() => handleRemoveLocalization('league', index)}>
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                            </Grid>
                        ))}

                        <Grid item xs={12}>
                            <Button 
                                startIcon={<AddIcon />} 
                                onClick={() => handleAddLocalization('league')}
                                size="small"
                            >
                                Add League Localization
                            </Button>
                        </Grid>

                        {/* Stadium Section */}
                        <Grid item xs={12}>
                            <Box display="flex" alignItems="center" mb={1} mt={3}>
                                <Typography variant="h6">Stadium</Typography>
                            </Box>
                        </Grid>
                        
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Stadium Name"
                                value={formData.stadium.name}
                                onChange={(e) => handleInputChange('stadium.name', e.target.value)}
                                sx={{ mb: 2 }}
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="City"
                                value={formData.stadium.city}
                                onChange={(e) => handleInputChange('stadium.city', e.target.value)}
                                sx={{ mb: 2 }}
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Country"
                                value={formData.stadium.country}
                                onChange={(e) => handleInputChange('stadium.country', e.target.value)}
                                sx={{ mb: 2 }}
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Geo Location"
                                value={formData.stadium.geoLocation}
                                onChange={(e) => handleInputChange('stadium.geoLocation', e.target.value)}
                                sx={{ mb: 2 }}
                            />
                        </Grid>

                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions>
                <Box display="flex" justifyContent="space-between" width="100%">
                    <Box>
                        {onDelete && (
                            <Button 
                                onClick={handleDeleteClick} 
                                color="error" 
                                variant="outlined"
                                startIcon={<DeleteIcon />}
                                disabled={isLoading || refetchLoading}
                            >
                                Delete Match
                            </Button>
                        )}
                    </Box>
                    <Box>
                        <Button onClick={handleClose} sx={{ mr: 1 }} disabled={isLoading || refetchLoading}>Close</Button>
                        <Button onClick={handleApply} variant="outlined" sx={{ mr: 1 }} disabled={isLoading || refetchLoading}>
                            Apply Changes
                        </Button>
                        <Button onClick={handleSave} variant="contained" disabled={isLoading || refetchLoading}>
                            Save Changes
                        </Button>
                    </Box>
                </Box>
            </DialogActions>

            {/* Loading Overlay */}
            <Backdrop
                sx={{ 
                    color: '#fff', 
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    position: 'absolute',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)'
                }}
                open={isLoading}
            >
                <Box display="flex" flexDirection="column" alignItems="center">
                    <CircularProgress color="inherit" />
                    <Typography variant="body2" sx={{ mt: 2 }}>
                        Updating data...
                    </Typography>
                </Box>
            </Backdrop>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete this match? This action cannot be undone.
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} disabled={isLoading}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm} color="error" disabled={isLoading}>Delete</Button>
                </DialogActions>
            </Dialog>

            {/* Error Toast */}
            <Snackbar
                open={errorMessage !== null}
                autoHideDuration={4000}
                onClose={() => setErrorMessage(null)}
                message={errorMessage}
            />
        </Dialog>
    );
};

export default EditMatchModal;